import { QueryClient } from '@tanstack/react-query';

const getErrorStatus = (error: unknown): number | undefined => {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const s = (error as { status?: unknown }).status;
    return typeof s === 'number' ? s : undefined;
  }
  return undefined;
};

const shouldRetryQuery = (failureCount: number, error: unknown): boolean => {
  const status = getErrorStatus(error);

  // Never retry definitive client errors — they will not resolve on a second
  // attempt (404 = not found, 409 = conflict, 422 = unprocessable, etc.).
  // 401/403 are handled separately via auth redirect logic in httpClient.
  if (status !== undefined && status >= 400 && status < 500) {
    return false;
  }

  return failureCount < 2;
};

/**
 * Never retry 4xx client errors — they indicate a definitive rejection by the
 * server (e.g. 409 Conflict, 422 Unprocessable Entity) that will not resolve
 * on a second attempt.  Allow up to 2 retries for 5xx / network failures.
 */
const shouldRetryMutation = (failureCount: number, error: unknown): boolean => {
  const status = getErrorStatus(error);

  if (status !== undefined && status >= 400 && status < 500) {
    return false;
  }

  return failureCount < 2;
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      networkMode: 'online',
      staleTime: 0, // Always consider data stale - no caching
      gcTime: 0, // Don't keep unused data in cache
      retry: shouldRetryQuery,
      refetchOnWindowFocus: false,
    },
    mutations: {
      networkMode: 'online',
      retry: shouldRetryMutation,
    },
  },
});

// Mutation defaults for consistent optimistic updates
queryClient.setMutationDefaults(['entities', 'create'], {
  mutationFn: async (_variables: unknown) => {
    throw new Error('Mutation function must be provided');
  },
});

queryClient.setMutationDefaults(['entities', 'update'], {
  mutationFn: async (_variables: unknown) => {
    throw new Error('Mutation function must be provided');
  },
});

queryClient.setMutationDefaults(['entities', 'delete'], {
  mutationFn: async (_variables: unknown) => {
    throw new Error('Mutation function must be provided');
  },
});
